import { useRef } from 'react';
import {
    Badge,
    Box,
    Text,
    Accordion,
    AccordionItem,
    AccordionButton,
    AccordionPanel,
    AccordionIcon,
} from '@chakra-ui/react';
import {
    CloseIcon,
    ArrowBackIcon,
    DownloadIcon,
    InfoOutlineIcon,
} from '@chakra-ui/icons';
import { Heading, Button } from '@/components/v4';
import { IGNORED_MESSAGE } from './constants';
import AGGridMain from '@/components/tables/AGGrid/AGGridMain';

function useVerifyCSVTableView({ verificationResult }) {
    const passedData = verificationResult?.passed?.data ?? [];
    const failedData = verificationResult?.failed?.data ?? [];

    // "Modifications" means anything that will actually be submitted —
    // edits AND deletes — so it's everything that isn't explicitly marked
    // unchanged, rather than an exact match on one specific status string
    // (which silently dropped delete-type rows, e.g. RATE_DELETED, from
    // both buckets).
    const filteredUnchanged = passedData.filter(
        (v) => v.verificationStatus === IGNORED_MESSAGE,
    );
    const filteredModifications = passedData.filter(
        (v) => v.verificationStatus !== IGNORED_MESSAGE,
    );

    // If no rows match either expected status (e.g. Number Blocks doesn't use verificationStatus),
    // treat all passed rows as modifications so the Submit button is enabled.
    const hasClassifiedRows =
        filteredModifications.length > 0 || filteredUnchanged.length > 0;

    const modifications = hasClassifiedRows
        ? filteredModifications
        : passedData;
    const unchanged = hasClassifiedRows ? filteredUnchanged : [];
    const ignored = failedData;

    const hasModifications = modifications.length > 0;

    return {
        modifications,
        unchanged,
        ignored,
        hasModifications,
        isImportDisabled: !hasModifications,
    };
}

const CountBadge = ({ count, colorScheme }) => (
    <Badge
        colorScheme={colorScheme}
        borderRadius="full"
        px={2}
        fontSize="xs"
        ml={2}>
        {count}
    </Badge>
);

const sectionHeaderStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    py: 2,
};

export default function VerifyCSVTableView(props) {
    const {
        verificationResult,
        handleCancel,
        handleImport,
        handleExportInvalidData,
        handleStartOver,
        passedColumns,
        failedColumns,
    } = props;

    const gridRef = useRef();
    const {
        modifications,
        unchanged,
        ignored,
        hasModifications,
        isImportDisabled,
    } = useVerifyCSVTableView({ verificationResult });

    return (
        <Box display="block">
            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={4}>
                <Heading size="xl">CSV Validation</Heading>
            </Box>

            <Accordion
                allowMultiple
                defaultIndex={hasModifications ? [0] : [1]}
                mb={4}>
                {/* MODIFICATIONS */}
                <AccordionItem
                    mb={2}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    overflow="hidden">
                    <AccordionButton
                        bg="gray.50"
                        _hover={{ bg: 'gray.100' }}
                        _expanded={{ bg: 'blue.50' }}>
                        <Box sx={sectionHeaderStyles}>
                            <Box display="flex" alignItems="center">
                                <Text
                                    fontWeight="bold"
                                    fontSize="sm"
                                    textTransform="uppercase"
                                    letterSpacing="wide"
                                    color="blue.700">
                                    Modifications
                                </Text>
                                <CountBadge
                                    count={modifications.length}
                                    colorScheme="blue"
                                />
                            </Box>
                            <AccordionIcon color="blue.600" />
                        </Box>
                    </AccordionButton>
                    <AccordionPanel pb={0} px={0}>
                        {modifications.length > 0 ? (
                            <Box
                                style={{
                                    maxHeight: '350px',
                                    overflowY: 'auto',
                                }}>
                                <AGGridMain
                                    gridRef={gridRef}
                                    isLoading={false}
                                    refetch={null}
                                    stateIdentifier="planCsvModifications"
                                    overlayNoRowsTemplate="No modifications found"
                                    tableData={{
                                        columns: passedColumns,
                                        data: modifications,
                                    }}
                                    suppressMenuHide={false}
                                    getQuickFilterText={null}
                                    columnDefs={passedColumns}
                                />
                            </Box>
                        ) : (
                            <Box px={4} py={3}>
                                <Text fontSize="sm" color="gray.500">
                                    No modifications to process.
                                </Text>
                            </Box>
                        )}
                    </AccordionPanel>
                </AccordionItem>

                {/* IGNORED RECORDS (validation failures) */}
                <AccordionItem
                    mb={2}
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    overflow="hidden">
                    <AccordionButton
                        bg="gray.50"
                        _hover={{ bg: 'gray.100' }}
                        _expanded={{ bg: 'orange.50' }}>
                        <Box sx={sectionHeaderStyles}>
                            <Box display="flex" alignItems="center">
                                <Text
                                    fontWeight="bold"
                                    fontSize="sm"
                                    textTransform="uppercase"
                                    letterSpacing="wide"
                                    color="orange.700">
                                    Ignored Records
                                </Text>
                                <CountBadge
                                    count={ignored.length}
                                    colorScheme="orange"
                                />
                            </Box>
                            <Box display="flex" alignItems="center" gap={2}>
                                {ignored.length > 0 && (
                                    <Button
                                        size="xs"
                                        leftIcon={<DownloadIcon />}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleExportInvalidData();
                                        }}>
                                        Export
                                    </Button>
                                )}
                                <AccordionIcon color="orange.600" />
                            </Box>
                        </Box>
                    </AccordionButton>
                    <AccordionPanel pb={0} px={0}>
                        {ignored.length > 0 ? (
                            <Box
                                style={{
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                }}>
                                <AGGridMain
                                    gridRef={gridRef}
                                    isLoading={false}
                                    refetch={null}
                                    stateIdentifier="planCsvIgnored"
                                    overlayNoRowsTemplate="No ignored records"
                                    tableData={{
                                        columns: failedColumns,
                                        data: ignored,
                                    }}
                                    suppressMenuHide={false}
                                    getQuickFilterText={null}
                                    columnDefs={failedColumns}
                                />
                            </Box>
                        ) : (
                            <Box px={4} py={3}>
                                <Text fontSize="sm" color="gray.500">
                                    No ignored records.
                                </Text>
                            </Box>
                        )}
                    </AccordionPanel>
                </AccordionItem>

                {/* UNCHANGED RECORDS */}
                <AccordionItem
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    overflow="hidden">
                    <AccordionButton
                        bg="gray.50"
                        _hover={{ bg: 'gray.100' }}
                        _expanded={{ bg: 'gray.100' }}>
                        <Box sx={sectionHeaderStyles}>
                            <Box display="flex" alignItems="center">
                                <Text
                                    fontWeight="bold"
                                    fontSize="sm"
                                    textTransform="uppercase"
                                    letterSpacing="wide"
                                    color="gray.600">
                                    Unchanged Records
                                </Text>
                                <CountBadge
                                    count={unchanged.length}
                                    colorScheme="gray"
                                />
                            </Box>
                            <AccordionIcon color="gray.500" />
                        </Box>
                    </AccordionButton>
                    <AccordionPanel pb={0} px={0}>
                        {unchanged.length > 0 ? (
                            <Box
                                style={{
                                    maxHeight: '300px',
                                    overflowY: 'auto',
                                }}>
                                <AGGridMain
                                    gridRef={gridRef}
                                    isLoading={false}
                                    refetch={null}
                                    stateIdentifier="planCsvUnchanged"
                                    overlayNoRowsTemplate="No unchanged records"
                                    tableData={{
                                        columns: passedColumns,
                                        data: unchanged,
                                    }}
                                    suppressMenuHide={false}
                                    getQuickFilterText={null}
                                    columnDefs={passedColumns}
                                />
                            </Box>
                        ) : (
                            <Box px={4} py={3}>
                                <Text fontSize="sm" color="gray.500">
                                    No unchanged records.
                                </Text>
                            </Box>
                        )}
                    </AccordionPanel>
                </AccordionItem>
            </Accordion>

            {/* Info note */}
            <Box
                display="flex"
                alignItems="flex-start"
                gap={2}
                p={3}
                mb={5}
                bg="blue.50"
                borderRadius="md"
                border="1px solid"
                borderColor="blue.200">
                <InfoOutlineIcon color="blue.500" mt="2px" flexShrink={0} />
                <Text fontSize="sm" color="blue.800">
                    Please review the segregated sections above. Entries with
                    errors or no changes will not be processed. The actual
                    import will only attempt when you click submit.
                </Text>
            </Box>

            {/* Action buttons */}
            {isImportDisabled ? (
                <Box display="flex" justifyContent="left">
                    <Button
                        leftIcon={<ArrowBackIcon boxSize={3} />}
                        onClick={handleStartOver}>
                        Start Over
                    </Button>
                </Box>
            ) : (
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center">
                    <Button
                        variant="outline"
                        leftIcon={<CloseIcon boxSize={2.5} />}
                        onClick={handleCancel}>
                        Back to upload
                    </Button>
                    <Box display="flex" alignItems="center" gap={3}>
                        <Badge colorScheme="red" fontSize="xs">
                            This cannot be undone.
                        </Badge>
                        <Button
                            leftIcon={<DownloadIcon />}
                            onClick={handleImport}>
                            Submit
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
